/**
 * Sales Stats tab (Tabs-updates batch). A data-set dropdown + a date-range
 * filter (today / this week / this month / this year / custom) driving a
 * Recharts bar chart. The chart is dynamically imported so Recharts is
 * code-split out of the main bundle.
 *
 * Reference: docs/plans/leads-opps-tabs-updates.md section 2.8.
 */

import { Suspense, lazy, useMemo, useState } from 'react';

import { startOfPeriod, cn } from '@/lib/utils';
import { useSalesStats } from '@/lib/api/sales';

const StatsChart = lazy(() => import('./StatsChart'));

/** Metric catalogue — keys match the backend STATS_METRICS. */
const METRICS: { key: string; label: string }[] = [
  { key: 'leads_by_stage', label: 'Leads by stage' },
  { key: 'leads_by_current_action', label: 'Leads by current action' },
  { key: 'leads_by_source', label: 'Leads by source' },
  { key: 'leads_by_industry', label: 'Leads by industry' },
  { key: 'leads_conversion', label: 'Lead conversion' },
  { key: 'opps_by_stage', label: 'Opportunities by stage' },
  { key: 'opps_by_current_action', label: 'Opportunities by current action' },
  { key: 'opps_pipeline_value', label: 'Pipeline value (open)' },
  { key: 'opps_win_rate', label: 'Win / loss' },
  { key: 'opps_won_value', label: 'Won value' },
];

type Period = 'today' | 'week' | 'month' | 'year' | 'custom';

const PERIODS: { key: Period; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'custom', label: 'Custom' },
];

/** Convert a YYYY-MM-DD to a UTC start-of-day ISO datetime. */
function startIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
}
/** Convert a YYYY-MM-DD to a UTC end-of-day ISO datetime. */
function endIso(dateStr: string): string {
  return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
}

export function StatsView() {
  const [metric, setMetric] = useState(METRICS[0]!.key);
  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const { fromIso, toIso, valid } = useMemo(() => {
    if (period === 'custom') {
      if (!customFrom || !customTo) return { fromIso: '', toIso: '', valid: false };
      return { fromIso: startIso(customFrom), toIso: endIso(customTo), valid: true };
    }
    // Brisbane-aware period start; window runs to "now".
    const from = startIso(startOfPeriod(period));
    return { fromIso: from, toIso: new Date().toISOString(), valid: true };
  }, [period, customFrom, customTo]);

  const { data, isLoading, isError } = useSalesStats(
    metric,
    valid ? fromIso : '1970-01-01T00:00:00.000Z',
    valid ? toIso : '1970-01-01T00:00:00.000Z',
  );

  return (
    <div className="flex flex-col gap-mx-4 pb-mx-6">
      <div className="flex flex-wrap items-end gap-mx-4 border-b border-mx-grey-800 pb-mx-4">
        <label className="flex flex-col gap-mx-1">
          <span className="mono-label text-mx-grey-mid">DATA SET</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="h-9 border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-mx-1">
          <span className="mono-label text-mx-grey-mid">RANGE</span>
          <div className="flex flex-wrap gap-mx-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={cn(
                  'mono-label border border-mx-grey-800 bg-mx-card px-mx-3 py-[6px] text-mx-mono-sm transition-colors',
                  period === p.key
                    ? 'border-mx-orange bg-mx-orange/10 text-mx-orange'
                    : 'text-mx-grey-mid hover:text-mx-grey-300',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {period === 'custom' && (
          <div className="flex items-end gap-mx-2">
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">FROM</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-9 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">TO</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-9 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
          </div>
        )}
      </div>

      <section className="border border-mx-grey-800 bg-mx-card p-mx-4">
        {!valid ? (
          <p className="text-mx-body-sm text-mx-grey-mid">Pick a custom from + to date.</p>
        ) : isLoading ? (
          <p className="mono-label text-mx-grey-mid">LOADING…</p>
        ) : isError || !data ? (
          <p className="text-mx-body-sm text-mx-neg">Could not load stats.</p>
        ) : (
          <Suspense fallback={<p className="mono-label text-mx-grey-mid">LOADING CHART…</p>}>
            <StatsChart points={data.points} currency={data.kind === 'currency'} />
          </Suspense>
        )}
      </section>
    </div>
  );
}
